import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Banknote,
  Building2,
  BriefcaseBusiness,
  Home,
  Building,
  Users,
  TrendingUp,
  BadgeDollarSign,
  Wallet,
  Percent,
  Landmark,
} from "lucide-react";

const iconByMetric = {
  "Construction GDP": Banknote,
  "Construction Share of State GDP": Percent,
  "No.of Construction Establishments": BriefcaseBusiness,
  "Nonresidential Spending": Building,
  "Residential Spending": Home,
  Employment: Users,
  "Annual Job Growth (YoY)": TrendingUp,
  "Projected Wage Increase (2026)": BadgeDollarSign,
  "Average Annual Wage": Wallet,
  "Unemployment Rate (Industry)": Landmark,
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return value.toLocaleString();
  return String(value);
};

const EconomicImpact = ({ data, title = "Utah vs. U.S. Economic Impact (2025)" }) => {
  const [showTable, setShowTable] = useState(false);

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data
      .filter(Boolean)
      .map((r) => ({
        metric: r.metric ?? "Metric",
        utah: r.utah_2025,
        us: r.us_2025,
      }));
  }, [data]);

  if (rows.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 mt-8"
      aria-labelledby="economic-impact-title"
    >
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-4">
        <div>
          <h2 id="economic-impact-title" className="text-xl font-black text-gray-900 m-0">
            {title}
          </h2>
          <p className="text-gray-500 text-sm mt-1 mb-0">
            Quick comparison cards first (easy scan), then an optional detailed table.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowTable((s) => !s)}
          className="px-4 py-2 rounded-full border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm font-semibold transition-all"
          aria-expanded={showTable}
        >
          {showTable ? "Hide table" : "Show table"}
        </button>
      </div>

      {/* Infographic cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((row, idx) => {
          const Icon = iconByMetric[row.metric] || Building2;
          return (
            <motion.div
              key={`${row.metric}-${idx}`}
              whileHover={{ y: -3 }}
              className="rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-5 shadow-sm"
            >
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="p-2 rounded-xl bg-indigo-50 text-indigo-700">
                  <Icon size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-gray-400">
                    Metric
                  </div>
                  <div className="font-extrabold text-gray-900 leading-tight truncate">
                    {row.metric}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-700">
                    Utah (2025)
                  </div>
                  <div className="text-base font-black text-indigo-900 mt-1 leading-snug">
                    {formatValue(row.utah)}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-600">
                    U.S. (2025)
                  </div>
                  <div className="text-base font-black text-gray-900 mt-1 leading-snug">
                    {formatValue(row.us)}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Optional detailed table */}
      {showTable && (
        <div className="mt-6 rounded-2xl border border-gray-100 overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="text-xs font-bold uppercase tracking-widest text-gray-500">
              Detailed table
            </div>
            <div className="text-xs text-gray-500">
              Tip: scroll horizontally on small screens
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-white">
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-gray-500 font-bold border-b border-gray-100">
                    Metric
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-gray-500 font-bold border-b border-gray-100">
                    Utah 2025
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-gray-500 font-bold border-b border-gray-100">
                    U.S. 2025
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={`tbl-${row.metric}-${idx}`} className="hover:bg-blue-50/40 transition-colors">
                    <td className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-100">
                      {row.metric}
                    </td>
                    <td className="px-4 py-3 font-extrabold text-indigo-800 border-b border-gray-100">
                      {formatValue(row.utah)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">
                      {formatValue(row.us)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.section>
  );
};

export default EconomicImpact;

